import catalogue from '../../../shared/feature-types.json';
import type { GeometryKind } from '../geo/types';
import type { Status } from '../lib/types';

export type PropKind =
  | 'text'
  | 'textarea'
  | 'int'
  | 'number'
  | 'bool'
  | 'enum'
  | 'enumMulti'
  | 'color'
  | 'featureRef'
  | 'featureRefs'
  | 'catalog'
  | 'list';

export interface PropDef {
  name: string;
  kind: PropKind;
  options?: string[];
  default?: string | number | boolean;
  design?: boolean;
  unityId?: boolean;
  catalog?: string;
  refTypes?: string[];
  items?: PropDef[];
}

export interface UnityMapping {
  component: string;
  scene: 'master' | 'persistent' | 'asset';
  idField: string | null;
  idFromKey?: 'key' | 'slug' | 'prefix_slug';
}

export interface FeatureTypeDef {
  id: string;
  group: string;
  keySegment: string;
  geometries: GeometryKind[];
  icon: string;
  color: string;
  showInGameDefault: boolean;
  parentTypes?: string[];
  unity: UnityMapping | null;
  props: PropDef[];
}

export interface Preset {
  id: string;
  type: string;
  geometry: GeometryKind;
  props: Record<string, string | number | boolean>;
}

type Raw = typeof catalogue;

export const STATUSES = catalogue.statuses as Status[];
export const GROUPS = catalogue.groups as string[];

export const TYPES: Record<string, FeatureTypeDef> = Object.fromEntries(
  Object.entries(catalogue.types as unknown as Record<string, Omit<FeatureTypeDef, 'id'>>).map(
    ([id, def]) => [id, { id, ...def }],
  ),
);
export const TYPE_IDS = Object.keys(TYPES);
export const PRESETS = (catalogue as Raw).presets as unknown as Preset[];

export function typeDef(type: string): FeatureTypeDef {
  const def = TYPES[type];
  if (!def) throw new Error(`Unknown feature type ${type}`);
  return def;
}

export function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[åä]/g, 'a')
      .replace(/ö/g, 'o')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'unnamed'
  );
}

/** Mirrors keyFor() in pb_hooks/lib/common.js: startland.<slug> for zones, startland.<segment>.<slug> otherwise. */
export function keyFor(type: string, prefix: string, slug: string): string {
  const seg = typeDef(type).keySegment;
  return seg ? `${prefix}.${seg}.${slug}` : `${prefix}.${slug}`;
}

export function uniqueKey(base: string, taken: Set<string>): string {
  let key = base;
  for (let i = 2; taken.has(key); i++) key = `${base}_${i}`;
  return key;
}

export function defaultProps(type: string): {
  properties: Record<string, unknown>;
  designProps: Record<string, unknown>;
} {
  const properties: Record<string, unknown> = {};
  const designProps: Record<string, unknown> = {};
  for (const p of typeDef(type).props) {
    if (p.default === undefined) continue;
    (p.design ? designProps : properties)[p.name] = p.default;
  }
  return { properties, designProps };
}

export const KEY_PATTERN = /^[a-z0-9_]+(\.[a-z0-9_]+)+$/;
